// Play (spec/15 P-10): the live session in full (the countdown against the server's clock, what is paying for it in
// order, the open call), its controls and the passes the player can buy there from their wallet; and below, the visits
// played. Not playing: what to do instead. No time is added from the phone (owner, 2026-10-11): that is the PC's.

import type { MyLiveSession, MyPlaySessionEntry } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, EmptyState, Skeleton } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useLiveSession, usePasses, usePlayHistory } from '../api/queries';
import { formatterFor, shortDuration } from '../lib/format';
import { LoadFailure, LoadingRegion, RowsSkeleton, SectionHead } from '../shell/feedback';
import { NavIcon } from '../shell/nav-icons';
import { Page } from '../shell/Page';
import { PassesToBuy } from './BuyPass';
import {
  CallStaffDialog,
  EndDialog,
  LiveRing,
  LockDialog,
  LockRequestNote,
  lockPending,
  statusBadge,
  useLiveActions,
  useLiveLeft,
} from './live';

export function PlayPage(): ReactNode {
  const { t } = useTranslation('player');
  const live = useLiveSession();

  return (
    <Page title={t('play.title')} description={t('play.subtitle')}>
      {live.isPending ? (
        <LoadingRegion className="pw-panel">
          <Skeleton shape="circle" width={200} height={200} />
          <Skeleton shape="text" lines={3} />
        </LoadingRegion>
      ) : live.isError ? (
        <LoadFailure error={live.error} onRetry={() => void live.refetch()} />
      ) : live.data.live === undefined ? (
        <div className="pw-panel">
          <EmptyState
            icon={<NavIcon name="monitor" size={32} />}
            title={t('play.none.title')}
            description={t('play.none.body')}
            action={
              <Link to="/venues" className="pw-btn pw-btn--primary">
                {t('play.none.find')}
              </Link>
            }
          />
        </div>
      ) : (
        <LiveSession live={live.data.live} refreshing={live.isFetching} />
      )}
      <History />
    </Page>
  );
}

function LiveSession({ live, refreshing }: { readonly live: MyLiveSession; readonly refreshing: boolean }): ReactNode {
  const { t } = useTranslation('player');
  const left = useLiveLeft(live) ?? live.session.timeLeftSeconds;
  const actions = useLiveActions();
  const [dialog, setDialog] = useState<'lock' | 'call' | 'end' | null>(null);
  const fmt = formatterFor(live.session.funding?.items.find((item) => item.amount !== null)?.amount?.currency ?? 'INR');
  const locked = live.session.status === 'locked';
  const grace = live.session.status === 'grace';
  const call = live.call;

  return (
    <section className="pw-play" aria-labelledby="pw-play-where">
      <div className="pw-play__hero" data-status={live.session.status}>
        <div className="pw-play__ring">
          <LiveRing live={live} left={left} size={208} />
        </div>
        <div className="pw-play__facts">
          <div className="pw-play__status">
            {statusBadge(live, t)}
            {refreshing ? <span className="pw-subtle">{t('play.live.updating')}</span> : null}
          </div>
          <h2 className="pw-play__where" id="pw-play-where">
            {t('play.live.on', { station: live.station.name, venue: live.venue.name })}
          </h2>
          <p className="pw-muted">
            {grace && live.session.graceEndsAt !== undefined
              ? t('play.live.graceBody', { time: fmt.time(live.session.graceEndsAt, 'short') })
              : live.session.fundedEndAt === null
                ? t('play.live.noEnd')
                : t('play.live.endsAt', { time: fmt.time(live.session.fundedEndAt, 'short') })}
          </p>
          {locked ? <p className="pw-play__note">{t('play.live.lockedBody')}</p> : null}
          <LockRequestNote live={live} />
          {call === undefined ? null : (
            <div className="pw-callstate" role="status">
              <NavIcon name="bell" size={18} />
              <span>
                {call.state === 'acknowledged'
                  ? call.acknowledgedByName === null
                    ? t('play.call.acknowledgedNoName')
                    : t('play.call.acknowledged', { name: call.acknowledgedByName })
                  : t('play.call.open')}
              </span>
              <Button variant="ghost" size="sm" busy={actions.cancelCall.isPending} onClick={() => actions.cancelCall.mutate()}>
                {t('play.call.cancelCall')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="pw-controls" role="group" aria-label={t('play.actions.title')}>
        {live.remoteLock ? (
          locked ? (
            <Button
              variant="primary"
              size="lg"
              icon={<NavIcon name="unlock" size={20} />}
              busy={actions.unlock.isPending || lockPending(live)}
              onClick={() => actions.unlock.mutate()}
            >
              {t('play.actions.unlock')}
            </Button>
          ) : (
            <Button variant="secondary" size="lg" icon={<NavIcon name="lock" size={20} />} busy={lockPending(live)} onClick={() => setDialog('lock')}>
              {t('play.actions.lock')}
            </Button>
          )
        ) : null}
        <Button variant="secondary" size="lg" icon={<NavIcon name="bell" size={20} />} disabled={call !== undefined} onClick={() => setDialog('call')}>
          {t('play.actions.callStaff')}
        </Button>
        {live.remoteLock ? (
          <Button variant="ghost" size="lg" icon={<NavIcon name="power" size={20} />} onClick={() => setDialog('end')}>
            {t('play.actions.end')}
          </Button>
        ) : null}
      </div>

      <Funding live={live} />
      <PassesToBuy live={live} />

      <LockDialog open={dialog === 'lock'} onOpenChange={(open) => setDialog(open ? 'lock' : null)} />
      <CallStaffDialog live={live} open={dialog === 'call'} onOpenChange={(open) => setDialog(open ? 'call' : null)} />
      <EndDialog live={live} open={dialog === 'end'} onOpenChange={(open) => setDialog(open ? 'end' : null)} />
    </section>
  );
}

/** What pays for the time left, in the order it is used, with "Use next" on a queued pass. */
function Funding({ live }: { readonly live: MyLiveSession }): ReactNode {
  const { t } = useTranslation('player');
  const { pin } = useLiveActions();
  const passes = usePasses();
  const funding = live.session.funding;
  if (funding === undefined || funding.items.length === 0) return null;
  const queued = new Set((passes.data?.active ?? []).filter((pass) => pass.place === 'queued').map((pass) => pass.id));

  return (
    <section className="pw-panel" aria-labelledby="pw-funding">
      <SectionHead id="pw-funding" title={t('play.funding.title')} />
      <ol className="pw-funding">
        {funding.items.map((item, index) => {
          const name =
            item.kind === 'pass' ? t('play.funding.pass', { name: item.name ?? '' }) : t(`play.funding.${item.kind}`);
          const canPin = item.kind === 'pass' && item.holdingId !== null && index > 0 && queued.has(item.holdingId);
          return (
            <li key={`${item.kind}-${item.holdingId ?? index}`} className="pw-funding__item">
              <span className="pw-funding__name">
                {name}
                {index === 0 ? <Badge tone="session">{t('play.funding.inUse')}</Badge> : null}
              </span>
              <span className="pw-funding__time clav-num">{shortDuration(item.seconds)}</span>
              {canPin ? (
                <Button size="sm" variant="ghost" icon={<NavIcon name="pin" size={16} />} busy={pin.isPending} onClick={() => pin.mutate(item.holdingId!)}>
                  {t('play.funding.useNext')}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="pw-subtle">
        {funding.then.kind === 'grace' && funding.then.seconds > 0
          ? t('play.funding.then', { minutes: Math.round(funding.then.seconds / 60) })
          : t('play.funding.thenEnd')}
      </p>
    </section>
  );
}

function endedWords(entry: MyPlaySessionEntry, t: (key: string, values?: Record<string, unknown>) => string): string {
  if (entry.endedAt === null) return t('play.history.inProgress');
  const reason = entry.endReason ?? 'other';
  return ['logout', 'ended_by_player', 'time_up', 'grace_expired', 'staff_ended'].includes(reason)
    ? t(`play.history.ended.${reason}`)
    : t('play.history.ended.other');
}

function History(): ReactNode {
  const { t } = useTranslation('player');
  const history = usePlayHistory();
  const rows = history.data?.pages.flatMap((page) => page.data) ?? [];
  const fmt = formatterFor('INR');

  return (
    <section className="pw-panel" aria-labelledby="pw-history">
      <SectionHead id="pw-history" title={t('play.history.title')} />
      {history.isPending ? (
        <RowsSkeleton />
      ) : history.isError ? (
        <LoadFailure error={history.error} onRetry={() => void history.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState size="compact" title={t('play.history.empty')} description={t('play.history.emptyBody')} />
      ) : (
        <>
          <ul className="pw-rows">
            {rows.map((entry) => (
              <li key={entry.id} className="pw-row">
                <span className="pw-row__icon" aria-hidden="true">
                  <NavIcon name="monitor" size={20} />
                </span>
                <span className="pw-row__main">
                  <span className="pw-row__title">
                    {entry.stationName} · {entry.venue.name}
                  </span>
                  <span className="pw-row__meta">
                    {fmt.dateTime(entry.startedAt, { date: 'medium', time: 'short' })} · {endedWords(entry, t)}
                  </span>
                </span>
                <span className="pw-row__end clav-num">{shortDuration(entry.playedSeconds)}</span>
              </li>
            ))}
          </ul>
          {history.hasNextPage ? (
            <Button variant="secondary" block busy={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>
              {t('play.history.more')}
            </Button>
          ) : null}
        </>
      )}
    </section>
  );
}
